import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from '../context/ThemeContext';

import LandingAIBot from './LandingAIBot';

const DetailPane = ({
  selectedFile,
  selectedCaseStudy,
  ABOUT,
  SKILLS,
  CASE_STUDIES,
  PROFILE_DATA,
  isMobile = false
}) => {
  const [markdownContent, setMarkdownContent] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const { colors } = useTheme();

  // Handle markdown file loading
  React.useEffect(() => {
    if (selectedFile?.endsWith('.md')) {
      setIsLoading(true);
      setMarkdownContent('');

      fetch(`/thoughts/${selectedFile.split('/').pop()}`)
        .then(response => response.text())
        .then(text => setMarkdownContent(text))
        .catch(error => console.error('Error loading markdown file:', error))
        .finally(() => setIsLoading(false));
    } else {
      setMarkdownContent('');
    }
  }, [selectedFile]);

  if (!selectedFile && !selectedCaseStudy) {
    return (
      <div className="hidden lg:block w-[508px] overflow-hidden" style={{ height: 'calc(100vh - 66px)', maxHeight: 'calc(100vh - 66px)' }}>
        <LandingAIBot />
      </div>
    );
  }

  const renderMarkdownContent = () => {
    if (isLoading) {
      return <div className="p-4 text-gray-500">Loading...</div>;
    }

    if (!markdownContent) {
      return <div className="p-4 text-gray-500">No content available</div>;
    }

    return (
      <div className="p-4">
        {!isMobile && (
          <div
            className="relative mb-[35px]"
            style={{
              borderTop: `1px dashed ${colors.border}`,
              borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
              borderImageSlice: 1
            }}
          />
        )}
        <div className="prose prose-xl max-w-none" style={{ color: colors.textSecondary }}>
          <ReactMarkdown
            components={{
              h1: (props) => <h1 className="text-4xl font-bold mb-5 mt-7 leading-tight" style={{ color: colors.textPrimary }} {...props} />,
              h2: (props) => <h2 className="text-3xl font-bold mb-4 mt-6 border-b pb-2 leading-tight" style={{ color: colors.textPrimary, borderColor: colors.border }} {...props} />,
              h3: (props) => <h3 className="text-2xl font-semibold mb-3 mt-5 leading-tight" style={{ color: colors.textPrimary }} {...props} />,
              p: (props) => <p className="mb-4 text-xl leading-tight" style={{ color: colors.textSecondary }} {...props} />,
              ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-1.5" style={{ color: colors.textSecondary }} {...props} />,
              ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-1.5" style={{ color: colors.textSecondary }} {...props} />,
              li: (props) => <li className="text-xl leading-tight" {...props} />,
              strong: (props) => <strong className="font-bold" style={{ color: colors.textPrimary }} {...props} />,
              em: (props) => <em className="italic" style={{ color: colors.textPrimary }} {...props} />,
              a: (props) => (
                <a
                  className="hover:opacity-80 underline transition-colors"
                  style={{ color: colors.accent }}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...props}
                >
                  {props.children}
                </a>
              ),
              hr: (props) => <hr className="my-8 border-t-2" style={{ borderColor: colors.border }} {...props} />,
              code: ({ inline, className, children, ...props }) => {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <div className="my-4 rounded-md overflow-hidden">
                    <SyntaxHighlighter
                      style={atomDark}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  </div>
                ) : (
                  <code className="px-1.5 py-0.5 rounded text-sm font-mono" style={{ backgroundColor: colors.surface, color: colors.textPrimary }} {...props}>
                    {children}
                  </code>
                );
              },
              blockquote: (props) => (
                <blockquote className="border-l-4 pl-5 italic my-5 text-xl leading-tight" style={{ borderColor: colors.border, color: colors.textSecondary }} {...props} />
              ),
            }}
            remarkPlugins={[]}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    // Markdown files from Thoughts folder
    if (selectedFile?.endsWith('.md')) {
      return renderMarkdownContent();
    }

    // About Me Section
    if (selectedFile === 'about_me.pdf') {
      return (
        <div className="p-4">
          {!isMobile && (
            <div
              className="relative mb-[35px]"
              style={{
                borderTop: `1px dashed ${colors.border}`,
                borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
                borderImageSlice: 1
              }}
            />
          )}
          <div className="max-w-full">
            <div className="mb-6 rounded-xl overflow-hidden relative" style={{ height: '300px' }}>
              <video
                src="/video.mp4"
                alt={PROFILE_DATA?.name}
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center center' }}
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
            <div className="mb-8">
              <p className="text-[20px] leading-snug mb-4" style={{ color: colors.textSecondary }}>{ABOUT?.intro ?? ''}</p>
              <p className="text-[20px] leading-snug mb-4" style={{ color: colors.textSecondary }}>{ABOUT?.description ?? ''}</p>
            </div>
            <div>
              <h2 className="text-[20px] font-semibold mb-4" style={{ color: colors.textSecondary }}>Expertise & Skills:</h2>
              <ul className="space-y-2">
                {SKILLS?.map((skill, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2" style={{ color: colors.textSecondary }}>•</span>
                    <span className="text-[20px]" style={{ color: colors.textSecondary }}>{skill}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      );
    }

    // Skills Section
    if (selectedFile === 'skills.pdf') {
      const skills = [
        'Product Strategy',
        'Roadmapping',
        'Feature Prioritization',
        'Growth & GTM',
        'OKRs',
        'JTBD',
        'User Research',
        'Data-Driven Execution',
        'A/B Testing',
        'Design Systems',
        'Agile Delivery',
        'Cross-Functional Leadership',
        'First-Principles Thinking'
      ];

      return (
        <div className="p-4">
          {!isMobile && (
            <div
              className="relative"
              style={{
                borderTop: `1px dashed ${colors.border}`,
                borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
                borderImageSlice: 1
              }}
            />
          )}
          <div className="max-w-full">
            <ul className="space-y-0">
              {skills.map((skill, index, arr) => (
                <li key={skill}>
                  <div className="flex items-center py-3">
                    <span className="mr-3" style={{ color: colors.textSecondary }}>•</span>
                    <span className="text-[20px]" style={{ color: colors.textSecondary }}>{skill}</span>
                  </div>
                  <div
                    className="h-0"
                    style={{
                      borderTop: `1px dashed ${colors.border}`,
                      borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
                      borderImageSlice: 1,
                      opacity: index === arr.length - 1 ? 1 : 1
                    }}
                  ></div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    if (selectedFile === 'tools.pdf') {
      return (
        <div className="p-4">
          {!isMobile && (
            <div
              className="relative"
              style={{
                borderTop: `1px dashed ${colors.border}`,
                borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
                borderImageSlice: 1
              }}
            ></div>
          )}
          <div className="max-w-full -mx-4 -mt-4">
            <ul className="space-y-0">
              {[
                { name: 'Figma', icon: '/tools/figma.svg' },
                { name: 'Webflow', icon: '/tools/webflow.svg' },
                { name: 'Miro', icon: '/tools/miro.svg' },
                { name: 'Notion', icon: '/tools/notion.svg' },
                { name: 'Framer', icon: '/tools/framer.jpeg' },
                { name: 'Amplitude', icon: '/tools/amplitude.svg' },
                { name: 'Mixpanel', icon: '/tools/mixpanel.svg' },
                { name: 'Lovable', icon: '/tools/lovable.svg' },
                { name: 'Cursor', icon: '/tools/cursor.jpeg' },
                { name: 'Windsurf', icon: '/tools/windsurf.jpeg' },
              ].map((tool, index, arr) => (
                <li key={tool.name}>
                  <div className={`flex items-center py-3 px-4 ${index === 0 ? 'mt-4' : ''}`}>
                    <img
                      src={tool.icon}
                      alt={tool.name}
                      className="flex-shrink-0 mr-3"
                      style={{ width: '11px', height: '11px' }}
                    />
                    <span className="text-[20px]" style={{ color: colors.textSecondary }}>{tool.name}</span>
                  </div>
                  <div
                    className="h-0 mx-4"
                    style={{
                      borderTop: `1px dashed ${colors.border}`,
                      borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
                      borderImageSlice: 1,
                      opacity: index === arr.length - 1 ? 1 : 1
                    }}
                  ></div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    if (selectedFile === 'experience.pdf') {
      return (
        <div className="p-4">
          {!isMobile && (
            <div
              className="relative mb-[35px]"
              style={{
                borderTop: `1px dashed ${colors.border}`,
                borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
                borderImageSlice: 1
              }}
            ></div>
          )}
          <div className="max-w-full">
            <div className="space-y-8">
              {ABOUT?.experiences?.map((experience, index) => (
                <div key={index}>
                  <h2 className="text-[24px] font-bold mb-4" style={{ color: colors.textPrimary }}>{experience.title}</h2>
                  <p className="text-[20px] leading-snug mb-4" style={{ color: colors.textSecondary }}>
                    {experience?.description ?? ''}
                  </p>
                  <ul className="space-y-3 ml-4">
                    {experience?.details?.map((detail, index) => (
                      <li key={index} className="text-[20px] leading-snug" style={{ color: colors.textSecondary }}>
                        • {detail}
                      </li>
                    )) ?? <></>}
                  </ul>
                </div>
              )) ?? <></>}
            </div>
          </div>
        </div>
      );
    }

    if (selectedFile === 'manga_me.exe') {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: "'PP Neue Bit', monospace", color: colors.textPrimary }}>COMING SOON</h2>
            <p className="text-xl" style={{ color: colors.textSecondary }}>Manga Me is currently under development.</p>
          </div>
        </div>
      );
    }

    if (selectedCaseStudy && CASE_STUDIES?.['Projects']?.[selectedCaseStudy]) {
      return (
        <div className="p-4">
          <div className="max-w-full">
            <div className="mb-6 rounded-xl overflow-hidden relative" style={{ height: '300px' }}>
              <img
                src={CASE_STUDIES?.['Projects']?.[selectedCaseStudy]?.banner ?? ''}
                alt={selectedCaseStudy}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="prose prose-xl max-w-none" style={{ color: colors.textSecondary }}>
              <ReactMarkdown
                components={{
                  h1: (props) => <h1 className="text-4xl font-bold mb-5 mt-7 leading-tight" style={{ color: colors.textPrimary }} {...props} />,
                  h2: (props) => <h2 className="text-3xl font-bold mb-4 mt-6 border-b pb-2 leading-tight" style={{ color: colors.textPrimary, borderColor: colors.border }} {...props} />,
                  h3: (props) => <h3 className="text-2xl font-semibold mb-3 mt-5 leading-tight" style={{ color: colors.textPrimary }} {...props} />,
                  p: (props) => <p className="mb-4 text-xl leading-tight" style={{ color: colors.textSecondary }} {...props} />,
                  ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-1.5" style={{ color: colors.textSecondary }} {...props} />,
                  ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-1.5" style={{ color: colors.textSecondary }} {...props} />,
                  li: (props) => <li className="text-xl leading-tight" {...props} />,
                  strong: (props) => <strong className="font-bold" style={{ color: colors.textPrimary }} {...props} />,
                  em: (props) => <em className="italic" style={{ color: colors.textPrimary }} {...props} />,
                  a: (props) => (
                    <a
                      className="hover:opacity-80 underline transition-colors"
                      style={{ color: colors.accent }}
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    />
                  ),
                  code: ({ inline, className, children, ...props }) => {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <div className="my-4 rounded-md overflow-hidden">
                        <SyntaxHighlighter
                          style={atomDark}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <code className="px-1.5 py-0.5 rounded text-sm font-mono" style={{ backgroundColor: colors.surface, color: colors.textPrimary }} {...props}>
                        {children}
                      </code>
                    );
                  },
                  blockquote: (props) => (
                    <blockquote className="border-l-4 pl-5 italic my-5 text-xl leading-tight" style={{ borderColor: colors.border, color: colors.textSecondary }} {...props} />
                  ),
                }}
                remarkPlugins={[]}
              >
                {CASE_STUDIES?.['Projects']?.[selectedCaseStudy]?.summary ?? ''}
              </ReactMarkdown>

              <a
                href={CASE_STUDIES?.['Projects']?.[selectedCaseStudy]?.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block underline text-[20px] hover:opacity-80"
                style={{ color: colors.textPrimary }}
              >
                Read full report
              </a>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const content = (
    <div className="h-full overflow-y-auto overflow-x-hidden" style={{ scrollbarWidth: 'thin', scrollbarColor: `${colors.border} ${colors.background}` }}>
      {renderContent()}
    </div>
  );

  if (isMobile) {
    return content;
  }

  return (
    <div className="hidden lg:block w-[508px] overflow-hidden" style={{ height: 'calc(100vh - 66px)', maxHeight: 'calc(100vh - 66px)' }}>
      {content}
    </div>
  );
};

export default DetailPane;
